import { useCallback } from "react";
import {
  useNavigation,
  CommonActions,
  StackActions,
  type NavigationProp,
} from "@react-navigation/native";
import type { MainTabParamList } from "../types";
import type { RootStackParamList } from "../../App";

type SmartBackOptions = {
  // Tab the user came from when this screen was entered via cross-tab
  // navigation. When present, back routes to that tab regardless of the
  // local stack. Named to match the existing `returnTab` convention used
  // by ArticleDetail.
  returnTab?: keyof MainTabParamList;
  // Route to land on when there's no local back stack and no `returnTab`.
  // Either a main tab (parent is the tab navigator) or a root-level route such
  // as `Profile` for the You stack (parent is the root navigator); both are
  // reached the same way — navigate the parent to this route, optionally with
  // an inner `fallbackScreen`.
  fallbackTab: keyof MainTabParamList | keyof RootStackParamList;
  // Optional inner screen of the fallback tab.
  fallbackScreen?: string;
};

export function useSmartBack({
  returnTab,
  fallbackTab,
  fallbackScreen,
}: SmartBackOptions) {
  const navigation = useNavigation();

  return useCallback(() => {
    const parent =
      navigation.getParent<
        NavigationProp<MainTabParamList & RootStackParamList>
      >();
    const state = navigation.getState();

    if (returnTab && parent) {
      // Reset the origin tab's local stack to its root before switching tabs.
      // Otherwise, a later tap on this tab's bar icon reopens the detail we
      // just left, and the stale `returnTab` in that detail's route would
      // trap the user in a loop back to the origin tab.
      if (state && state.index > 0) {
        navigation.dispatch(StackActions.popToTop());
      }
      parent.dispatch(CommonActions.navigate({ name: returnTab }));
      return;
    }

    // Pop the local stack when it has somewhere to pop to. We deliberately do
    // NOT use `navigation.canGoBack()`: that also returns true when a *parent*
    // (tab/root) navigator can go back, so on a web reload / deep link straight
    // into this screen — where the local stack holds only this one route —
    // `goBack()` would bubble up to the parent and land on the app's initial
    // route (Home) instead of the intended parent. The local stack index is
    // the precise "can this navigator pop?" check; otherwise fall through to
    // the explicit fallback below.
    if (state && state.index > 0) {
      navigation.goBack();
      return;
    }

    if (parent) {
      parent.dispatch(
        CommonActions.navigate({
          name: fallbackTab,
          params: fallbackScreen ? { screen: fallbackScreen } : undefined,
        }),
      );
    }
  }, [navigation, returnTab, fallbackTab, fallbackScreen]);
}

const TAB_LABELS: Record<keyof MainTabParamList, string> = {
  Home: "Home",
  Care: "Care",
  Costs: "Costs",
  Access: "Access",
  Learn: "Learn",
  Chat: "Chat",
};

export function getBackLabel(
  returnTab: keyof MainTabParamList | undefined,
  fallbackTab: keyof MainTabParamList,
  translatedBackLabel: string,
): string {
  return returnTab ? translatedBackLabel : TAB_LABELS[fallbackTab];
}
